import { useMemo, useState } from 'react';
import type { ContrastIssue } from '@noorview/core';
import { checkContrast, issuesKey } from '@noorview/core';
import { Icon } from '../ui/icons';
import { useStudio } from './store';

/**
 * Readability help: warns when widgets would be hard to read on the chosen background and offers a one-click fix.
 * The editor can dismiss the warnings; new problems still show.
 */

const KEY = 'noorview.contrast.dismissed';
function dismissed(designId: string | null): string {
  try { return (JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, string>)[designId ?? ''] ?? ''; } catch { return ''; }
}
export function dismiss(designId: string | null, key: string) {
  try { const all = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Record<string, string>; all[designId ?? ''] = key; localStorage.setItem(KEY, JSON.stringify(all)); } catch { /* ignore */ }
}

export function useContrast() {
  const design = useStudio((s) => s.design);
  return useMemo(() => (design ? checkContrast(design) : []), [design]);
}

/** Problems the editor hasn't dismissed (used to confirm before sending live). */
export function openIssues(): ContrastIssue[] {
  const st = useStudio.getState();
  if (!st.design) return [];
  const issues = checkContrast(st.design);
  return issuesKey(issues) && issuesKey(issues) === dismissed(st.designId) ? [] : issues;
}

function applyFix(i: ContrastIssue) {
  const st = useStudio.getState();
  if (i.fix?.widget && i.widgetId) st.updateWidget(i.widgetId, i.fix.widget);
  if (i.fix?.background) st.change((d) => ({ ...d, background: i.fix!.background! }));
}

const GROUP_TEXT: Record<ContrastIssue['kind'], (n: number) => string> = {
  edges: (n) => `${n} widgets blend into the background, so their edges disappear.`,
  text: (n) => `${n} widgets have text that’s hard to read on this background.`,
  accent: (n) => `${n} widgets have a highlight colour that’s hard to see.`,
  image: () => 'Glass widgets over a photo can be hard to read.',
};
/** Same problem with the same fix on several widgets is shown once, with one button for all of them. */
function groups(issues: ContrastIssue[]) {
  const map = new Map<string, ContrastIssue[]>();
  for (const i of issues) { const k = `${i.kind}|${i.fix?.label ?? ''}`; map.set(k, [...(map.get(k) ?? []), i]); }
  return [...map.values()].map((items) => ({ items, message: items.length > 1 ? GROUP_TEXT[items[0]!.kind](items.length) : items[0]!.message }));
}

export function ContrastNotice() {
  const issues = useContrast();
  const designId = useStudio((s) => s.designId);
  const [open, setOpen] = useState(false);
  const [, force] = useState(0);
  const key = issuesKey(issues);
  if (!issues.length || dismissed(designId) === key) return null;
  const problems = issues.filter((i) => i.severity === 'problem').length;
  const fixable = issues.filter((i) => i.fix);
  return (
    <div className={`contrast${problems ? ' bad' : ''}`} role="status">
      <div className="row">
        <Icon name="alert" size={18} />
        <b style={{ flex: 1 }}>{issues.length === 1 ? 'One widget may be hard to read' : `${issues.length} widgets may be hard to read`} on this background</b>
        <button type="button" className="link" onClick={() => setOpen((v) => !v)} aria-expanded={open}>{open ? 'Hide' : 'Details'}</button>
        {fixable.length > 1 && <button type="button" className="btn sm primary" onClick={() => fixable.forEach(applyFix)}>Fix all</button>}
        <button type="button" className="btn sm plain" onClick={() => { dismiss(designId, key); force((n) => n + 1); }} title="Keep my choice">Ignore</button>
      </div>
      {open && (
        <ul>
          {groups(issues).map((g, k) => (
            <li key={k}>
              <button type="button" className="link plain-link" onClick={() => g.items[0]?.widgetId && useStudio.getState().select(g.items[0].widgetId)}>{g.message}</button>
              {g.items[0]?.fix && <button type="button" className="btn sm plain" onClick={() => g.items.forEach(applyFix)}>{g.items[0].fix.label}{g.items.length > 1 ? ` (${g.items.length})` : ''}</button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function WidgetIssues({ id }: { id: string }) {
  const issues = useContrast().filter((i) => i.widgetId === id);
  if (!issues.length) return null;
  return (
    <div className="contrast compact">
      {issues.map((i, k) => (
        <div key={k} className="ci">
          <p><Icon name="alert" size={16} /><span>{i.short}</span></p>
          {i.fix && <button type="button" className="btn sm primary block" onClick={() => applyFix(i)}>{i.fix.label}</button>}
        </div>
      ))}
    </div>
  );
}
